/* 
  Behavior for sections/store-locator.liquid.
  Independent of the rest of the theme — fetches its own store list from
  assets/store-locations.json (URL passed via the section's
  data-locations-url attribute), NOT window.RETAILS/shop.metafields.custom.
  locations. Deliberate: decouples this section from the metafield so the
  store list doesn't need to be pasted into Shopify's metafield editor by
  hand — see the comment in sections/store-locator.liquid for the trade-off
  this implies (the file, not the metafield, becomes the source of truth
  for this section specifically). Owns its own Leaflet map instance per
  section; no shared state with nav-menu.js, tramas-nav.js, etc.

  Data requirements on each entry of that file's `locations` array — same
  shape window.RETAILS.locations already carries elsewhere in the theme
  (id, name, address1, address2, city, province, zip, country), plus:
  - lat, lng: {Number} — required. Entries without both are skipped, since
    there is no reliable way to place or sort them without coordinates.
  - location_precision: {String} "exact" | "approximate" — optional; when
    "approximate", the list item shows a small "Ubicación aproximada" note
    (used for stores whose exact address could not be geocoded and instead
    fall back to their city/postcode center — see the one-time geocoding
    script used to backfill this metafield, not part of the theme).
  - hours: {Object|undefined} — optional, keyed by lowercase English weekday
    ("monday".."sunday"), each either { open: "HH:MM", close: "HH:MM" } or
    omitted/null for a closed day. When absent, the list simply omits the
    hours/open-closed line for that store (no data is invented).

  Geocoding (search box only — never re-geocodes stores, see above) uses
  Nominatim (OpenStreetMap), triggered only on an explicit "Buscar" click or
  Enter — not on every keystroke — to stay within their fair-use expectations
  for a small, low-volume storefront search.
*/
(function () {
  var EARTH_RADIUS_KM = 6371;
  var COUNTRY_TIMEZONES = { Spain: 'Europe/Madrid', Portugal: 'Europe/Lisbon', Italy: 'Europe/Rome' };
  var WEEKDAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  // Used only if the section's data-strings attribute is missing/malformed —
  // otherwise overridden per storefront language from locales/*.json's
  // "sections.store_locator" keys (set in sections/store-locator.liquid).
  var DEFAULT_STRINGS = {
    searching: 'Buscando…',
    noResults: 'No se ha encontrado esa ubicación. Prueba con otra búsqueda.',
    searchError: 'No se ha podido completar la búsqueda. Inténtalo de nuevo.',
    geolocationUnsupported: 'Tu navegador no permite compartir tu ubicación.',
    geolocating: 'Obteniendo tu ubicación…',
    geolocationError: 'No se ha podido obtener tu ubicación.',
    currentLocation: 'tu ubicación actual',
    nearbyTo: 'Tiendas por cercanía a %s',
    openNow: 'Abierto ahora',
    closedNow: 'Cerrado ahora',
    todayHours: 'Hoy: %s',
    approximateLocation: 'Ubicación aproximada',
    noStores: 'No hay tiendas disponibles en este momento.',
    countryLabels: { Spain: 'España', Portugal: 'Portugal', Italy: 'Italia' },
  };

  function haversineKm(lat1, lon1, lat2, lon2) {
    var toRad = function (d) { return (d * Math.PI) / 180; };
    var dLat = toRad(lat2 - lat1);
    var dLon = toRad(lon2 - lon1);
    var a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    return EARTH_RADIUS_KM * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  // Locale-aware decimal separator (e.g. "2,2 km" in es/it/fr/pt vs
  // "2.2 km" in en) — matches whatever language the storefront is
  // currently rendering in (<html lang>), not hardcoded to Spanish.
  function formatDistanceKm(km) {
    var lang = document.documentElement.lang || 'es';
    var formatted;
    try {
      formatted = new Intl.NumberFormat(lang, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(km);
    } catch (err) {
      formatted = km.toFixed(1);
    }
    return formatted + ' km';
  }

  function escapeHtml(value) {
    var div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }

  // Reads "now" in the store's own country timezone, not the visitor's —
  // Portugal (Europe/Lisbon) differs from Spain/Italy, so this matters here.
  function getStoreNow(country) {
    var tz = COUNTRY_TIMEZONES[country] || 'Europe/Madrid';
    var parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      weekday: 'long',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date());
    var weekday = '';
    var hour = '0';
    var minute = '0';
    parts.forEach(function (part) {
      if (part.type === 'weekday') weekday = part.value.toLowerCase();
      if (part.type === 'hour') hour = part.value;
      if (part.type === 'minute') minute = part.value;
    });
    return { weekday: weekday, minutes: parseInt(hour, 10) * 60 + parseInt(minute, 10) };
  }

  function getOpenState(store) {
    if (!store.hours) return null;
    var now = getStoreNow(store.country);
    var today = store.hours[now.weekday];
    if (!today || !today.open || !today.close) {
      return { isOpen: false, todayLabel: null };
    }
    var openParts = today.open.split(':');
    var closeParts = today.close.split(':');
    var openMinutes = parseInt(openParts[0], 10) * 60 + parseInt(openParts[1], 10);
    var closeMinutes = parseInt(closeParts[0], 10) * 60 + parseInt(closeParts[1], 10);
    return {
      isOpen: now.minutes >= openMinutes && now.minutes < closeMinutes,
      todayLabel: today.open + '–' + today.close,
    };
  }

  async function initStoreLocator(root) {
    if (root.dataset.storeLocatorInitialized === 'true') return;
    root.dataset.storeLocatorInitialized = 'true';

    var listEl = root.querySelector('[data-store-locator-list]');
    var mapEl = root.querySelector('[data-store-locator-map]');
    var inputEl = root.querySelector('[data-store-locator-input]');
    var searchBtn = root.querySelector('[data-store-locator-search]');
    var geoBtn = root.querySelector('[data-store-locator-geolocate]');
    var statusEl = root.querySelector('[data-store-locator-status]');
    var summaryEl = root.querySelector('[data-store-locator-summary]');
    var listHeadingEl = root.querySelector('[data-store-locator-list-heading]');
    var defaultListHeading = listHeadingEl ? listHeadingEl.dataset.headingDefault : '';
    var nearbyListHeading = listHeadingEl ? listHeadingEl.dataset.headingNearby : '';

    var parsedStrings = null;
    try {
      parsedStrings = JSON.parse(root.dataset.strings || 'null');
    } catch (err) {
      parsedStrings = null;
    }
    var strings = Object.assign({}, DEFAULT_STRINGS, parsedStrings || {});
    strings.countryLabels = Object.assign(
      {},
      DEFAULT_STRINGS.countryLabels,
      (parsedStrings && parsedStrings.countryLabels) || {}
    );

    function formatAddress(store) {
      var parts = [store.address1, store.address2].filter(Boolean);
      var line = parts.join(', ');
      var countryLabel = strings.countryLabels[store.country] || store.country;
      return [line, store.zip, store.city, store.province, countryLabel].filter(Boolean).join(', ');
    }

    if (!listEl || !mapEl || typeof L === 'undefined') return;

    var allLocations = [];
    if (root.dataset.locationsUrl) {
      try {
        var response = await fetch(root.dataset.locationsUrl);
        if (response.ok) {
          var data = await response.json();
          if (data && Array.isArray(data.locations)) allLocations = data.locations;
        }
      } catch (err) {
        // allLocations stays [] — surfaced below via the "no stores" branch.
      }
    }

    var stores = allLocations.filter(function (s) {
      return typeof s.lat === 'number' && typeof s.lng === 'number' && !isNaN(s.lat) && !isNaN(s.lng);
    });

    if (!stores.length) {
      listEl.innerHTML = '<li class="store-locator__empty">' + escapeHtml(strings.noStores) + '</li>';
      mapEl.hidden = true;
      return;
    }

    var map = L.map(mapEl, { scrollWheelZoom: false });
    root._storeLocatorMap = map;
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors',
    }).addTo(map);

    var bounds = L.latLngBounds(stores.map(function (s) { return [s.lat, s.lng]; }));
    map.fitBounds(bounds, { padding: [30, 30] });

    var pinIcon = L.divIcon({
      className: 'store-locator__pin',
      html: '<svg viewBox="0 0 24 32" width="26" height="34" xmlns="http://www.w3.org/2000/svg"><path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 20 12 20s12-11 12-20c0-6.6-5.4-12-12-12z" fill="#767676"/><circle cx="12" cy="12" r="5" fill="#fff"/></svg>',
      iconSize: [26, 34],
      iconAnchor: [13, 34],
      popupAnchor: [0, -30],
    });

    var userIcon = L.divIcon({
      className: 'store-locator__user-pin',
      html: '<span class="store-locator__user-dot"></span>',
      iconSize: [16, 16],
      iconAnchor: [8, 8],
    });

    var markers = {};
    stores.forEach(function (store) {
      var marker = L.marker([store.lat, store.lng], { icon: pinIcon }).addTo(map);
      marker.bindPopup(
        '<strong>' + escapeHtml(store.name) + '</strong><br>' + escapeHtml(formatAddress(store))
      );
      marker.on('click', function () {
        focusStore(store.id, { scrollList: true, panMap: false });
      });
      markers[String(store.id)] = marker;
    });

    var userMarker = null;
    var referencePoint = null;

    function focusStore(id, opts) {
      opts = opts || {};
      var idStr = String(id);
      var current = listEl.querySelector('.store-locator__item.is-active');
      if (current) current.classList.remove('is-active');
      var li = listEl.querySelector('[data-store-id="' + idStr.replace(/"/g, '') + '"]');
      if (li) {
        li.classList.add('is-active');
        if (opts.scrollList) li.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
      var marker = markers[idStr];
      if (marker) {
        if (opts.panMap) {
          map.setView(marker.getLatLng(), Math.max(map.getZoom(), 14), { animate: true });
        }
        marker.openPopup();
      }
    }

    function sortedStores() {
      var list = stores.slice();
      if (referencePoint) {
        list.sort(function (a, b) { return a._distanceKm - b._distanceKm; });
      } else {
        list.sort(function (a, b) { return a.name.localeCompare(b.name); });
      }
      return list;
    }

    function renderList() {
      var list = sortedStores();
      listEl.innerHTML = '';
      list.forEach(function (store) {
        var li = document.createElement('li');
        li.className = 'store-locator__item';
        li.dataset.storeId = store.id;
        li.tabIndex = 0;
        li.setAttribute('role', 'button');

        var openState = getOpenState(store);
        var statusHtml = openState
          ? '<span class="store-locator__open-state ' + (openState.isOpen ? 'is-open' : 'is-closed') + '">' +
            escapeHtml(openState.isOpen ? strings.openNow : strings.closedNow) + '</span>'
          : '';
        var hoursHtml = (openState && openState.todayLabel)
          ? '<div class="store-locator__item-hours">' +
            escapeHtml(strings.todayHours.replace('%s', openState.todayLabel)) + '</div>'
          : '';
        var distanceHtml = referencePoint
          ? '<div class="store-locator__distance">' + formatDistanceKm(store._distanceKm) + '</div>'
          : '';
        var approxHtml = store.location_precision === 'approximate'
          ? '<div class="store-locator__approx">' + escapeHtml(strings.approximateLocation) + '</div>'
          : '';

        li.innerHTML =
          '<div class="store-locator__item-row">' +
            '<span class="store-locator__item-name">' + escapeHtml(store.name) + '</span>' +
            statusHtml +
          '</div>' +
          '<div class="store-locator__item-address">' + escapeHtml(formatAddress(store)) + '</div>' +
          hoursHtml +
          distanceHtml +
          approxHtml;

        li.addEventListener('click', function () { focusStore(store.id, { panMap: true }); });
        li.addEventListener('keydown', function (event) {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            focusStore(store.id, { panMap: true });
          }
        });
        listEl.appendChild(li);
      });
    }

    function setStatus(message) {
      if (!statusEl) return;
      statusEl.textContent = message || '';
      statusEl.hidden = !message;
    }

    function updateListHeading(label) {
      if (listHeadingEl) {
        listHeadingEl.textContent = label ? nearbyListHeading : defaultListHeading;
      }
      if (summaryEl) {
        if (label) {
          summaryEl.textContent = strings.nearbyTo.replace('%s', label);
          summaryEl.hidden = false;
        } else {
          summaryEl.textContent = '';
          summaryEl.hidden = true;
        }
      }
    }

    function placeUserMarker(lat, lng) {
      if (userMarker) {
        userMarker.setLatLng([lat, lng]);
      } else {
        userMarker = L.marker([lat, lng], { icon: userIcon, zIndexOffset: 1000 }).addTo(map);
      }
    }

    function setReferencePoint(lat, lng, label) {
      referencePoint = { lat: lat, lng: lng };
      stores.forEach(function (s) {
        s._distanceKm = haversineKm(lat, lng, s.lat, s.lng);
      });
      renderList();
      updateListHeading(label);
      placeUserMarker(lat, lng);
      var nearest = sortedStores()[0];
      if (nearest) {
        map.fitBounds(L.latLngBounds([[lat, lng], [nearest.lat, nearest.lng]]), { padding: [40, 40], maxZoom: 13 });
      }
      setStatus('');
    }

    async function geocodeQuery(query) {
      var url = new URL('https://nominatim.openstreetmap.org/search');
      url.searchParams.set('q', query);
      url.searchParams.set('format', 'json');
      url.searchParams.set('limit', '1');
      url.searchParams.set('countrycodes', 'es,pt,it');
      var response = await fetch(url.toString());
      if (!response.ok) throw new Error('geocode_failed');
      var data = await response.json();
      if (!data.length) return null;
      return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon), label: data[0].display_name };
    }

    async function handleSearch() {
      var query = inputEl ? inputEl.value.trim() : '';
      if (!query) return;
      setStatus(strings.searching);
      if (searchBtn) searchBtn.disabled = true;
      try {
        var geo = await geocodeQuery(query);
        if (!geo) {
          setStatus(strings.noResults);
          return;
        }
        setReferencePoint(geo.lat, geo.lng, geo.label);
      } catch (err) {
        setStatus(strings.searchError);
      } finally {
        if (searchBtn) searchBtn.disabled = false;
      }
    }

    if (searchBtn) searchBtn.addEventListener('click', handleSearch);
    if (inputEl) {
      inputEl.addEventListener('keydown', function (event) {
        if (event.key === 'Enter') {
          event.preventDefault();
          handleSearch();
        }
      });
    }

    if (geoBtn) {
      geoBtn.addEventListener('click', function () {
        if (!navigator.geolocation) {
          setStatus(strings.geolocationUnsupported);
          return;
        }
        setStatus(strings.geolocating);
        navigator.geolocation.getCurrentPosition(
          function (position) {
            setReferencePoint(position.coords.latitude, position.coords.longitude, strings.currentLocation);
          },
          function () {
            setStatus(strings.geolocationError);
          },
          { enableHighAccuracy: false, timeout: 10000 }
        );
      });
    }

    renderList();
  }

  function initAll() {
    document.querySelectorAll('.store-locator').forEach(initStoreLocator);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme Editor: a section added/edited after initial page load never sees
  // the DOMContentLoaded listener above, so it needs its own init call —
  // and the guard in initStoreLocator (data-store-locator-initialized) keeps
  // an edit that just re-renders the same instance from double-initializing.
  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target && event.target.querySelector ? event.target.querySelector('.store-locator') : null;
    if (root) initStoreLocator(root);
  });

  // Tears down the Leaflet instance before the Theme Editor discards the
  // old section markup, so it doesn't keep tracking a detached map/tiles.
  document.addEventListener('shopify:section:unload', function (event) {
    var root = event.target && event.target.querySelector ? event.target.querySelector('.store-locator') : null;
    if (root && root._storeLocatorMap) {
      root._storeLocatorMap.remove();
      root._storeLocatorMap = null;
    }
  });
})();
